import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, Input, Output, EventEmitter, ChangeDetectorRef, IterableDiffers, DoCheck } from '@angular/core';
import { ColumnMetadataModel } from '../../../../datawarehouse/models/ColumnMetadataModel';
import { Observable, Subject } from 'rxjs';
import { MatPaginator, MatSort, MatDialog, MatTooltip } from '@angular/material';
import { SearchComponent } from '../../../search/components/search.component';
import { PageEventDataSource } from '../../../../core/services/PageEventDataSource';
import { PageRequestModel } from '../../../../core/models/PageRequestModel';
import { FormGroup } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { RefreshListAction, REFRESH_LIST_TYPE } from '../../store/actions';
import * as moment from 'moment';

@Component({
  selector: 'plr-metadata-entity-list',
  templateUrl: './metadata-entity-list.component.html',
  styleUrls: ['./metadata-entity-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetadataEntityListComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['actions'];
  columns: ColumnMetadataModel[];
  dataSource: PageEventDataSource<any>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  _metadata: any;
  _metadataRefreshed$: Subject<boolean> = new Subject<boolean>();
  _initialized = false;
  _allExpanded = true;
  _sessionExpanded = false;
  _sessionEntities: FormGroup[];
  _lastPageRequest: PageRequestModel;
  _showingDeleted = false;

  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(SearchComponent)
  search: SearchComponent;

  @Input()
  set metadata(metadata: any) {
    this._metadataRefreshed$.next(true);
    this._metadataRefreshed$ = new Subject<boolean>();
    this._metadata = metadata;
    if (this._initialized) {
      this.metadataRefreshed();
    }
  }

  @Input()
  listOnly: boolean;

  @Input()
  loading: Observable<boolean>;

  @Input()
  items: Observable<any[]>;

  @Input()
  totalCount: Observable<number>;

  @Input()
  canAssociateToExisting: any;

  @Input()
  isAssociationToExistingContext: boolean;

  @Input()
  set sessionEntities(entities: FormGroup[]) {
    if (this._initialized && (!this._sessionEntities || this._sessionEntities.length === 0) && entities) {
      if (entities.length > 0) {
        this._sessionExpanded = true;
        this._allExpanded = false;
      } else {
        this._sessionExpanded = false;
        this._allExpanded = true;
      }
      this.changeDetector.markForCheck();
    }
    this._sessionEntities = entities;
  }

  @Input()
  editMode: boolean;

  @Output()
  showDeleted = new EventEmitter<any>();

  @Output()
  dontShowDeleted = new EventEmitter<any>();

  @Output()
  pageRequest = new EventEmitter<PageRequestModel>();

  @Output()
  itemSelected = new EventEmitter<any>();

  @Output()
  itemAdded = new EventEmitter<any>();

  @Output()
  deleteItem = new EventEmitter<any>();

  @Output()
  undoDeleteItem = new EventEmitter<any>();

  @Output()
  cloneItem = new EventEmitter<any>();

  @Output()
  itemAssociated = new EventEmitter<any>();

  @Output()
  searchForItemsForAssociation = new EventEmitter<any>();

  constructor(private dialog: MatDialog, private changeDetector: ChangeDetectorRef, private actions$: Actions) {}

  ngOnDestroy() {
    this.destroyed$.next(true);
    this._metadataRefreshed$.next(true);
  }

  ngOnInit() {
    this._initialized = true;
    if (this._sessionEntities && this._sessionEntities.length > 0) {
      this._sessionExpanded = true;
      this._allExpanded = false;
    }
    this.metadataRefreshed();
    this.search.searchControl.valueChanges.takeUntil(this.destroyed$).subscribe(() => {
      this._allExpanded = true;
    });

    this.actions$
      .ofType(REFRESH_LIST_TYPE)
      .takeUntil(this.destroyed$)
      .subscribe(() => this.refreshDataSource());
  }

  private metadataRefreshed() {
    if (this._metadata) {
      this.columns = [];
      this.displayedColumns = [];
      for (var property in this._metadata.propertyMetadataItems) {
        var propertyMetadata = this._metadata.propertyMetadataItems[property];
        this.columns.push({
          $type: propertyMetadata.$type,
          displayName: propertyMetadata.displayName,
          name: property,
        });
        if (propertyMetadata.listItem) {
          this.displayedColumns.push(property);
        }
      }
      this.displayedColumns.push('actions');
      this.refreshDataSource();
    }
  }

  private refreshDataSource() {
    this.dataSource = new PageEventDataSource<any>(
      this._metadataRefreshed$,
      this.paginator,
      this.items,
      this.totalCount,
      this.loading,
      r => {
        this._lastPageRequest = r;
        this.pageRequest.emit(r);
      },
      this.sort,
      this.search,
    );
  }

  selectItem(item) {
    this._sessionExpanded = true;
    this._allExpanded = false;
    this.itemSelected.emit(item);
  }

  addItem(): void {
    this.itemAdded.emit({});
  }

  delete(item): void {
    this._sessionExpanded = true;
    this.deleteItem.emit(item);
  }

  undoDelete(item): void {
    this._sessionExpanded = true;
    this.undoDeleteItem.emit(item);
  }

  clone(item): void {
    this._sessionExpanded = true;
    this.cloneItem.emit(item);
  }

  associateItem(item): void {
    this.itemAssociated.emit(item);
  }

  searchForItemsToAssociate(): void {
    this.searchForItemsForAssociation.emit({});
  }

  getDisplayValue(item: any) {
    if (!item) {
      return item;
    }
    if(item instanceof moment){
      return moment(item).format('M/D/YYYY');
    }
    return item.displayName || item.description || item;
  }

  onShowDeleted(): void {
    this._showingDeleted = true;
    this._sessionExpanded = false;
    this._allExpanded = true;
    this.showDeleted.emit(this._lastPageRequest);
  }

  onDontShowDeleted(): void {
    this._showingDeleted = false;
    this._sessionExpanded = false;
    this._allExpanded = true;
    this.dontShowDeleted.emit(this._lastPageRequest);
  }
}



// WEBPACK FOOTER //
// ./src/app/pillar/workspace/components/metadata-entity-list/metadata-entity-list.component.ts